import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Loader2, Sparkles } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

export default function Pricing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [me, setMe] = useState(null);
  const [annual, setAnnual] = useState(false);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/commerce/plans"), api.get("/commerce/me")])
      .then(([p, m]) => { setPlans(p.data); setMe(m.data); })
      .catch((e) => toast.error(apiErr(e)));
    api.post("/commerce/telemetry", { event: "pricing_viewed" }).catch(() => {});
  }, []);

  const upgrade = async (tier) => {
    const plan = plans.plans[tier];
    const lookup = annual ? plan.lookup_annual : plan.lookup_monthly;
    if (!lookup) return;
    setBusy(tier);
    try {
      const { data } = await api.post("/commerce/checkout", { lookup_key: lookup, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setBusy(null); }
  };

  if (!plans || !me) return <div className="p-8 text-tc-muted" data-testid="pricing-loading">Loading plans…</div>;
  const order = ["FREE", "PRO", "FAMILY"];

  return (
    <div className="p-6 lg:p-10 max-w-6xl mx-auto" data-testid="pricing-page">
      <div className="text-center">
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach">Plans</div>
        <h1 className="font-heading text-4xl font-extrabold mt-1">Analyze markets like a professional</h1>
        <p className="text-tc-sub mt-2 max-w-2xl mx-auto">TRADECRAFT teaches and assists market analysis. It does not predict prices, promise profits, or tell you what to trade — you keep the judgment.</p>
        <div className="inline-flex items-center gap-2 mt-5 rounded-lg border border-white/12 p-1" data-testid="billing-toggle">
          <button onClick={() => setAnnual(false)} className={`px-3 py-1.5 text-sm rounded ${!annual ? "bg-tc-coach text-tc-bg" : "text-tc-sub"}`}>Monthly</button>
          <button onClick={() => setAnnual(true)} className={`px-3 py-1.5 text-sm rounded ${annual ? "bg-tc-coach text-tc-bg" : "text-tc-sub"}`}>Annual <span className="text-[10px]">save ~25%</span></button>
        </div>
        <div className="text-[11px] text-tc-muted mt-2" data-testid="pricing-disclaimer">{plans.disclaimer}</div>
      </div>

      <div className="grid md:grid-cols-3 gap-5 mt-8">
        {order.map((tier) => {
          const p = plans.plans[tier];
          const price = annual ? p.price_annual : p.price_monthly;
          const isCurrent = me.tier === tier;
          const highlight = tier === "PRO";
          return (
            <div key={tier} data-testid={`plan-${tier}`} className={`tc-card p-6 flex flex-col ${highlight ? "border-tc-coach/50 ring-1 ring-tc-coach/30" : ""}`}>
              {highlight && <div className="text-[10px] font-mono uppercase tracking-widest text-tc-coach flex items-center gap-1"><Sparkles className="w-3 h-3" /> Most popular</div>}
              <div className="font-heading text-xl font-bold mt-1">{p.name}</div>
              <div className="mt-2" data-testid={`plan-price-${tier}`}>
                {annual && p.price_annual > 0 ? (
                  <><span className="text-3xl font-extrabold">${Math.round(p.price_annual / 12)}</span><span className="text-tc-muted text-sm">/mo</span>
                    <div className="text-[12px] text-tc-muted">billed ${p.price_annual}/yr</div></>
                ) : (
                  <><span className="text-3xl font-extrabold">${price}</span><span className="text-tc-muted text-sm">/{annual ? "yr" : "mo"}</span></>
                )}
              </div>
              <p className="text-tc-sub text-sm mt-2">{p.blurb}</p>
              <ul className="mt-4 space-y-2 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-tc-txt"><Check className="w-4 h-4 text-tc-coach mt-0.5 shrink-0" />{f}</li>
                ))}
              </ul>
              <div className="mt-5">
                {isCurrent ? (
                  <div className="text-center rounded-lg border border-white/15 py-2.5 text-tc-sub text-sm" data-testid={`current-${tier}`}>Current plan</div>
                ) : tier === "FREE" ? (
                  <button data-testid="plan-free-cta" onClick={() => navigate("/dashboard")} className="w-full rounded-lg border border-white/15 py-2.5 text-tc-sub hover:text-tc-txt">Start free</button>
                ) : (
                  <button data-testid={`upgrade-${tier}`} onClick={() => upgrade(tier)} disabled={busy === tier}
                    className="w-full rounded-lg bg-tc-coach text-tc-bg font-semibold py-2.5 flex items-center justify-center gap-2 disabled:opacity-60">
                    {busy === tier ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Upgrade to {p.name.replace("TRADECRAFT ", "")}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-center text-tc-muted text-xs mt-6">{plans.note}</p>
    </div>
  );
}
